import { FontAwesome5 } from '@expo/vector-icons';
import { LevelTestType } from '@openeducation/oe-rn-shared-types';
import {
  secondaryFontFamily,
  styled,
  ThemeType,
} from '@openeducation/oe-rn-themes';
import { format } from 'date-fns';
import { rgba } from 'polished';
import React, { memo } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';

import { OEText } from '../../atoms/oe-text/oe-text';

export interface LevelTestCardLabels {
  endOfLevel: string;
  midLevel: string;
  attempt: string;
}

export interface LevelTestCardProps {
  levelName: string;
  testType: LevelTestType;
  scorePercentage: number;
  attempt: number;
  labels: LevelTestCardLabels;
  isPassed?: boolean;
  date?: Date | null;
  style?: StyleProp<ViewStyle>;
}

export function LevelTestCard({
  levelName,
  testType,
  scorePercentage,
  attempt,
  labels,
  isPassed,
  date,
  style,
}: LevelTestCardProps) {
  const isTestTypeHighlighted = testType === LevelTestType.END_OF_LEVEL;

  const getTestTypeLabel = () => {
    switch (testType) {
      case LevelTestType.END_OF_LEVEL:
        return labels.endOfLevel;
      case LevelTestType.MID_LEVEL:
      default:
        return labels.midLevel;
    }
  };

  return (
    <Container style={style}>
      <LevelName>{levelName}</LevelName>
      <TestTypeWrapper isHighlighted={isTestTypeHighlighted}>
        <TestType isHighlighted={isTestTypeHighlighted}>
          {getTestTypeLabel().toUpperCase()}
        </TestType>
        {isPassed && <MedalIcon />}
      </TestTypeWrapper>
      <ScoreWrapper>
        <Score>{scorePercentage}%</Score>
      </ScoreWrapper>
      {date && <DateText>{format(date, "dd MMM',' yyyy")}</DateText>}
      <Attempt>
        {labels.attempt} {attempt}
      </Attempt>
    </Container>
  );
}

const textCommonProps = {
  textAlign: 'center' as const,
  fontFamily: secondaryFontFamily,
};

const Container = styled.View`
  shadow-radius: 5px;
  shadow-opacity: 0.1;
  shadow-color: ${({ theme }) => theme.color.black};
  shadow-offset: 2px 2px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.color.white};
  width: 160px;
  padding-vertical: 24px;
`;

const LevelName = styled(OEText).attrs({
  fontType: 'semiBold',
  fontSize: 20,
  lineHeight: 24,
  ...textCommonProps,
})`
  margin-bottom: 8px;
`;

type TestTypeWrapperType = {
  isHighlighted: boolean;
};

const TestTypeWrapper = styled.View<TestTypeWrapperType>`
  padding: 4px 8px;
  border-top-width: 1px;
  border-bottom-width: 1px;
  margin-bottom: 24px;
  align-self: center;
  align-items: center;
  ${({ theme, isHighlighted }) => {
    const borderColor = isHighlighted
      ? rgba(theme.color.primary, 0.2)
      : theme.color.border;
    return `
      border-top-color: ${borderColor};
      border-bottom-color: ${borderColor};
    `;
  }}
`;

type TestTypeType = {
  theme: ThemeType;
  isHighlighted: boolean;
};

const TestType = styled(OEText).attrs(
  ({ theme, isHighlighted }: TestTypeType) => ({
    color: isHighlighted ? theme.color.primary : theme.color.dark1,
    fontType: isHighlighted ? 'semiBold' : 'regular',
    fontSize: 12,
    lineHeight: 16,
    ...textCommonProps,
  })
)<TestTypeType>``;

const MEDAL_SIZE = 20;

const MedalIcon = styled(FontAwesome5).attrs(({ theme }) => ({
  color: theme.color.primary,
  name: 'medal',
  size: MEDAL_SIZE,
}))`
  position: absolute;
  bottom: -${MEDAL_SIZE + 2}px;
`;

const ScoreWrapper = styled.View`
  justify-content: center;
  align-items: center;
  padding-vertical: 8px;
  background-color: ${({ theme }) => theme.color.primary};
  margin-bottom: 16px;
`;

const Score = styled(OEText).attrs(({ theme }) => ({
  fontType: 'semiBold',
  fontSize: 32,
  lineHeight: 34,
  color: theme.color.white,
  ...textCommonProps,
}))``;

const DateText = styled(OEText).attrs({
  fontSize: 16,
  lineHeight: 16,
  ...textCommonProps,
})`
  margin-bottom: 16px;
`;

const Attempt = styled(OEText).attrs(({ theme }) => ({
  color: theme.color.dark3,
  fontSize: 12,
  lineHeight: 12,
  ...textCommonProps,
}))``;

export default memo(LevelTestCard);
